import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PROSE_REVEAL_STATEMENTS } from '../../data/content';
import { PixelGlyph } from '../common/PixelGlyph';

gsap.registerPlugin(ScrollTrigger);

export const GroqProseReveal: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<(HTMLParagraphElement | null)[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create GSAP ScrollTrigger timeline for scrubbing text reveal
    const ctx = gsap.context(() => {
      linesRef.current.forEach((line) => {
        if (!line) return;

        gsap.fromTo(
          line,
          {
            opacity: 0.15,
            color: '#71717a',
            y: 15,
          },
          {
            opacity: 1,
            color: '#f5f2eb',
            y: 0,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: line,
              start: 'top 80%',
              end: 'top 45%',
              scrub: 0.5,
            },
          }
        );
      });
    }, container);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="manifesto"
      ref={containerRef}
      className="relative py-16 sm:py-28 md:py-48 px-4 sm:px-6 max-w-5xl mx-auto z-10"
      aria-label="Engineering Manifesto"
    >
      {/* Eyebrow */}
      <div className="flex items-center gap-2.5 sm:gap-3 mb-10 sm:mb-16 text-xs font-mono tracking-widest text-sunset-gold uppercase">
        <PixelGlyph name="grid-dots" size={14} color="#ffaf01" />
        <span>MANIFESTO // THE PHILOSOPHY OF FRICTIONLESS SPEED</span>
      </div>

      {/* Scrubbing Kinetic Prose Lines */}
      <div className="space-y-8 sm:space-y-12 md:space-y-16">
        {PROSE_REVEAL_STATEMENTS.map((statement, idx) => {
          const isAccent = idx === 4 || idx === 8;
          return (
            <div key={idx} className="relative overflow-hidden">
              <p
                ref={(el) => (linesRef.current[idx] = el)}
                className={`font-display font-bold text-2xl sm:text-4xl md:text-5xl lg:text-[3.75rem] leading-[1.2] sm:leading-[1.18] tracking-tight will-change-transform ${
                  isAccent
                    ? 'text-gradient bg-gradient-to-r from-sunset-gold via-gold-radiance to-sunset-flame bg-clip-text text-transparent'
                    : 'text-cream-100'
                }`}
              >
                {statement}
              </p>
            </div>
          );
        })}
      </div>

      {/* Swiss architectural separator with domain mark */}
      <div className="mt-16 sm:mt-28 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs font-mono text-cream-dim">
        <div className="flex items-center gap-2">
          <PixelGlyph name="corner" size={10} color="#d4af37" />
          <span>SPECIMEN ID: EXP-TH157LE-2026</span>
        </div>
        <div>STANDARDS: ZERO REFLOWS • GPU COMPOSITING • W3C PERFORMANCE L3</div>
      </div>
    </section>
  );
};
